import React from 'react'
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { motion } from 'framer-motion'; 

import TopBanner from '../components/TopBanner';
import RightImageText from '../components/RightImageText';
import Badge from '../components/Badge';
import BestSolutions from '../components/BestSolutions';

export default function OurServices() {
    const homeimg = '/images/home/home.jpg'; 
    const server_url = process.env.REACT_APP_SERVER;

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "IT Solutions",
        "provider": {
          "@type": "Organization",
          "name": "Lurantech",
          "url": `${server_url}`,
          "logo": `${server_url}/images/logo.png`,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-7226-999-101",
            "contactType": "Customer Service",
            "areaServed": "IN",
            "availableLanguage": "English"
          },
          "sameAs": [
            "https://www.facebook.com/Lurantech-107968334690860",
            "https://www.linkedin.com/company/lurantech",
            "https://x.com/lurantech"
          ],
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "FF-401, 4th Floor, 7 Sky Complex, Laxmipura, Near VUDA Cross Road",
            "addressLocality": "Vadodara",
            "addressRegion": "Gujarat",
            "postalCode": "390021",
            "addressCountry": "IN"
          }
        },
        "description": "Lurantech offers tailored IT solutions to help your business succeed. Our services include Liferay CMS development, IT service management, cloud solutions, data engineering, application development, and more. We prioritize quality and flexibility in every project.",
        "serviceAudience": {
          "@type": "Audience",
          "audienceType": "Business Clients"
        },
        "offers": [
          {
            "@type": "Offer",
            "priceCurrency": "INR",
            "price": "Contact for pricing",
            "eligibleRegion": {
              "@type": "Place",
              "name": "India"
            },
            "url": `${server_url}/service`
          }
        ]
    }

    const headerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const logoVariants = {
        hidden: { opacity: 0, scale: 0.5 },
        visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 100 } }
    };

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <meta property="og:title" content="Lurantech | Services | Tailored IT Solutions for Your Success" />
                    <meta property="og:description" content="Lurantech offers top-quality IT services like Liferay CMS development, IT service management, cloud solutions, data engineering, and more. Tailored IT solutions for you." />
                    <meta property="og:image" content={`${server_url}${homeimg}`} />
                    <meta property="og:url" content={`${server_url}/service`} />
                    <meta property="og:type" content="website" />
                    <meta property="og:image:type" content="image/jpeg" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="675" />
                    <meta name="geo.position" content="22.3026;73.1812" />
                    <meta name="geo.placename" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />
                    <meta name="geo.region" content="IN-GJ" />
                    <meta property="og:latitude" content="22.3026" />
                    <meta property="og:longitude" content="73.1812" />
                    <meta property="og:location" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />

                    <meta name="twitter:title" content="Lurantech | Services | Tailored IT Solutions for Your Success" />
                    <meta name="twitter:description" content="Lurantech offers top-quality IT services like Liferay CMS development, IT service management, cloud solutions, data engineering, and more. Tailored IT solutions for you." />
                    <meta name="twitter:image" content={`${server_url}${homeimg}`} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta property="twitter:image:type" content="image/jpeg" />
                    <meta property="twitter:image:width" content="1200" />
                    <meta property="twitter:image:height" content="675" />

                    <meta name="robots" content="index, follow" />
                    <meta name="description" content="Lurantech offers top-quality IT services like Liferay CMS development, IT service management, cloud solutions, data engineering, and more. Tailored IT solutions for you." />
                    <meta name="keywords" content="Liferay CMS development, IT service management, cloud solutions, application development, data engineering, integration platforms, flexible cooperation, tailored IT solutions, e-commorce" />
                    <meta name="author" content="Lurantech" />

                    <link rel="canonical" href={`${server_url}/service`} />
                    <link rel="preload" href={`${server_url}${homeimg}`} as="image" />
                    <title>Lurantech | Services | Tailored IT Solutions for Your Success</title>
                </Helmet>

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(schemaData),
                    }}
                />

                <TopBanner title="OUR SERVICES" bgimage='/images/about-us/about.jpg'/>
                
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants}>
                    <RightImageText />
                </motion.div>

                <motion.div className="row g-0 py-4" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants}>
                    <div className='text-center head-service-text'>Featured Services</div>
                </motion.div>

                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants}>
                    <BestSolutions />
                </motion.div>

                <motion.div className="row g-0 py-4" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants}>
                    <div className='text-center head-service-text'>Our Expertised Area</div>
                </motion.div>

                {/* TECHNOLOGY LOGOS GRID */}
                <motion.div 
                    className="row g-0 logos-con container mx-auto"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                    {[
                        { id: "liferay", src: "/images/service/liferay.png", alt: "liferay", tip: "Liferay" },
                        { id: "aws", src: "/images/service/aws.svg", alt: "aws", tip: "Amazon Web Services" },
                        { id: "tomcat", src: "/images/service/tomcat.svg", alt: "tomcat", tip: "Apache tomcat" },
                        { id: "springboot", src: "/images/service/springboot.svg", alt: "springboot", tip: "Java Spring-Boot" },
                        { id: "dxpcloude", src: "/images/service/dxpcloude.jpg", alt: "dxpcloude", tip: "Liferay DXP cloud" }
                    ].map((logo) => (
                        <motion.div key={logo.id} className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 logos-div" variants={logoVariants}>
                            <img data-tooltip-id={logo.id} src={logo.src} alt={logo.alt} />
                            <ReactTooltip id={logo.id} place="bottom" content={logo.tip} />
                        </motion.div>
                    ))}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                    
                    {/* ... repeat for other logo rows using the same pattern ... */}
                    {/* Row 2 */}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                    {[
                        { id: "mysql", src: "/images/service/mysql.svg", alt: "mysql", tip: "MySQL Database" },
                        { id: "postgre", src: "/images/service/postgresql.svg", alt: "postgre", tip: "PostgreSQL Database" },
                        { id: "mongodb", src: "/images/service/mongodb.svg", alt: "mongodb", tip: "MongoDB" },
                        { id: "c", src: "/images/service/c.svg", alt: "c++", tip: "C++" },
                        { id: "java", src: "/images/service/java.svg", alt: "java", tip: "Java Technologies" }
                    ].map((logo) => (
                        <motion.div key={logo.id} className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 logos-div" variants={logoVariants}>
                            <img data-tooltip-id={logo.id} src={logo.src} alt={logo.alt} />
                            <ReactTooltip id={logo.id} place="bottom" content={logo.tip} />
                        </motion.div>
                    ))}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>

                    {/* Row 3 */}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                    {[
                        { id: "gstreamer", src: "/images/service/gstreamer.png", alt: "gstreamer", tip: "Gstreamer" },
                        { id: "ffmpeg", src: "/images/service/ffmpeg.png", alt: "ffmpeg", tip: "FFmpeg" },
                        { id: "rust", src: "/images/service/rust.svg", alt: "rust", tip: "Rust" },
                        { id: "go", src: "/images/service/go.svg", alt: "go", tip: "GoLang" },
                        { id: "react", src: "/images/service/react.svg", alt: "react", tip: "React JS" }
                    ].map((logo) => (
                        <motion.div key={logo.id} className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 logos-div" variants={logoVariants}>
                            <img data-tooltip-id={logo.id} src={logo.src} alt={logo.alt} />
                            <ReactTooltip id={logo.id} place="bottom" content={logo.tip} />
                        </motion.div>
                    ))}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>

                    {/* Row 4 */}
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                    {[
                        { id: "bootstrap", src: "/images/service/bootstrap.svg", alt: "bootstrap", tip: "Bootstrap" },
                        { id: "html", src: "/images/service/html.png", alt: "html", tip: "HTML 5" },
                        { id: "css", src: "/images/service/css.png", alt: "css", tip: "CSS" },
                        { id: "js", src: "/images/service/js.png", alt: "js", tip: "Java Script" }
                    ].map((logo) => (
                        <motion.div key={logo.id} className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 logos-div" variants={logoVariants}>
                            <img data-tooltip-id={logo.id} src={logo.src} alt={logo.alt} />
                            <ReactTooltip id={logo.id} place="bottom" content={logo.tip} />
                        </motion.div>
                    ))}
                    <div className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 logos-div"></div>
                    <div className="col-1 col-sm-1 col-md-1 col-lg-1 col-xl-1 logos-div"></div>
                </motion.div>

                {/* WHY OUR SERVICES SECTION - FIXED BADGE LAYOUT */}
                <div className='why-our-container container'>
                    <div className="row g-0">
                        <div className="col-12 col-sm-2 col-md-2 col-lg-2 col-xl-2"></div>
                        <motion.div 
                            className="col-12 col-sm-8 col-md-8 col-lg-8 col-xl-8" 
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={headerVariants}
                        >
                            <div className="why-our-title">Why Our Services are Better?</div>
                        </motion.div>
                        <div className="col-12 col-sm-2 col-md-2 col-lg-2 col-xl-2"></div>
                    </div>

                    <motion.div 
                        className="row g-0 why-our-con-lower" 
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        {/* We use motion.span or a div with NO classes to avoid breaking the Badge's internal col classes */}
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="Quality Comes First" badgeimg="/images/about-us/quality-comes.png" />
                        </motion.div>
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="Flexible Cooperation" badgeimg="/images/about-us/flexibal.png" />
                        </motion.div>
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="On-time Delivery" badgeimg="/images/about-us/on-time.png" />
                        </motion.div>
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="Qualified Developers" badgeimg="/images/about-us/qualified.png" />
                        </motion.div>
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="Transparent Costs" badgeimg="/images/about-us/transparent.png" />
                        </motion.div>
                        <motion.div variants={logoVariants} style={{ display: 'contents' }}>
                            <Badge badgetitle="Quick Scale-up" badgeimg="/images/about-us/quick-scale.png" />
                        </motion.div>
                    </motion.div>
                </div>
            </HelmetProvider>
        </>
    )
};